import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { P, rows, type PayComponent } from '@hrms/shared';
import { useAuth } from '../../../auth/AuthProvider';
import { api } from '../../../lib/api';
import { humanize } from '../../../lib/date';
import { fieldErrors } from '../../../lib/errors';
import { useMut } from '../../../lib/query';
import { Alert, Badge, Button, ConfirmDialog, DataTable, Modal, PageHeader, Screen, Select, Stack, Switch, Text, TextField, useTheme } from '../../../ui';
import { AutoGrid, CALCULATIONS, COMPONENT_TYPES, Mono, NumField, TYPE_TONE } from '../components/common';

type Form = Partial<PayComponent>;
const BLANK: Form = { code: '', name: '', type: 'earning', calculation: 'formula', formula: '', is_taxable: true, is_pf_wage: false, is_esi_wage: false, prorate: true, sort_order: 0 };
const CALC_HINT: Record<PayComponent['calculation'], string> = {
  formula: 'Evaluated from the formula, e.g. CTC * 0.40 / 12',
  fixed: 'A fixed monthly amount (enter the number as the formula).',
  balancing: 'CTC minus every other component (e.g. Special allowance).',
  statutory: 'Computed by the statutory engine (PF, ESI, PT, LWF, TDS).',
  input: 'Taken from monthly payroll inputs.',
};

export function PayComponentsScreen() {
  const { can } = useAuth();
  const { colors } = useTheme();
  const manage = can(P.PAYROLL_MANAGE);
  const [search, setSearch] = useState('');
  const q = useQuery({ queryKey: ['payroll', 'components'], queryFn: () => api.payroll.components.list({ per_page: 500 }) });
  const [f, setF] = useState<Form | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [del, setDel] = useState(false);
  const list = rows(q.data)
    .filter((c) => !search || `${c.code} ${c.name}`.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.sort_order - b.sort_order);

  const save = useMut(
    (b: Form) => {
      const body = { ...b, formula: b.formula || null };
      return b.id ? api.payroll.components.update(b.id, body) : api.payroll.components.create(body);
    },
    {
      invalidate: [['payroll', 'components']],
      success: 'Component saved',
      onSuccess: () => setF(null),
      onError: (e) => setErrs(fieldErrors(e)),
    },
  );
  const remove = useMut(() => api.payroll.components.remove(f!.id!), {
    invalidate: [['payroll', 'components']],
    success: 'Component deleted',
    onSuccess: () => {
      setDel(false);
      setF(null);
    },
  });
  const open = (c: PayComponent | null) => {
    setErrs({});
    setF(c ? { ...c } : { ...BLANK, sort_order: (list[list.length - 1]?.sort_order ?? 0) + 10 });
  };
  const needsFormula = f?.calculation === 'formula' || f?.calculation === 'fixed';

  return (
    <Screen refreshing={q.isRefetching} onRefresh={() => q.refetch()} testID="pay-components">
      <PageHeader
        title="Pay components"
        subtitle="Formulas can reference CTC, MONTHLY_CTC, IS_METRO and any other component code, e.g. BASIC * 0.5."
        actions={manage ? <Button variant="primary" icon="plus" title="New component" onPress={() => open(null)} testID="new-component" /> : undefined}
      />
      <TextField placeholder="Search code or name…" value={search} onChangeText={setSearch} accessibilityLabel="Search components" />
      <DataTable
        rows={list}
        loading={q.isLoading}
        onRowPress={manage ? open : undefined}
        emptyTitle="No pay components"
        columns={[
          { key: 'sort_order', title: '#', width: 56, align: 'right' },
          { key: 'code', title: 'Code', width: 110, render: (c) => <Mono>{c.code}</Mono>, sortValue: (c) => c.code },
          { key: 'name', title: 'Name', width: 180, flex: 2 },
          { key: 'type', title: 'Type', width: 170, render: (c) => <Badge tone={TYPE_TONE[c.type]}>{humanize(c.type)}</Badge>, sortValue: (c) => c.type },
          { key: 'calculation', title: 'Calculation', width: 110, render: (c) => humanize(c.calculation), sortValue: (c) => c.calculation },
          { key: 'formula', title: 'Formula', width: 200, flex: 2, render: (c) => (c.formula ? <Mono>{c.formula}</Mono> : '—'), sortable: false },
          {
            key: 'flags',
            title: 'Flags',
            width: 200,
            sortable: false,
            render: (c) => [c.is_taxable && 'Taxable', c.is_pf_wage && 'PF wage', c.is_esi_wage && 'ESI wage', c.prorate && 'Prorate'].filter(Boolean).join(' · ') || '—',
          },
        ]}
      />
      <Modal
        open={!!f}
        onClose={() => setF(null)}
        title={f?.id ? `Edit ${f.code}` : 'New pay component'}
        width={620}
        testID="component-form"
        footer={
          <>
            {!!f?.id && <Button variant="ghost" icon="trash" title="Delete" onPress={() => setDel(true)} />}
            <Button title="Cancel" onPress={() => setF(null)} />
            <Button variant="primary" title="Save" loading={save.isPending} onPress={() => f && save.mutate(f)} testID="save-component" />
          </>
        }
      >
        {f && (
          <Stack gap={14}>
            {errs.message && <Alert tone="danger">{errs.message}</Alert>}
            <AutoGrid min={220}>
              <TextField label="Code" required autoCapitalize="characters" placeholder="BASIC" value={f.code ?? ''} error={errs.code} hint="Uppercase letters, digits, underscore" onChangeText={(v) => setF({ ...f, code: v.toUpperCase().replace(/[^A-Z0-9_]/g, '') })} />
              <TextField label="Name" required placeholder="Basic salary" value={f.name ?? ''} error={errs.name} onChangeText={(v) => setF({ ...f, name: v })} />
              <Select label="Type" required value={f.type} error={errs.type} options={COMPONENT_TYPES.map((v) => ({ value: v, label: humanize(v) }))} onChange={(v) => setF({ ...f, type: v })} />
              <Select label="Calculation" required value={f.calculation} error={errs.calculation} hint={f.calculation ? CALC_HINT[f.calculation] : undefined} options={CALCULATIONS.map((v) => ({ value: v, label: humanize(v) }))} onChange={(v) => setF({ ...f, calculation: v })} />
            </AutoGrid>
            <TextField
              label={f.calculation === 'fixed' ? 'Amount' : 'Formula'}
              required={needsFormula}
              placeholder={f.calculation === 'fixed' ? '1600' : 'CTC * 0.40 / 12'}
              value={f.formula ?? ''}
              error={errs.formula}
              hint={needsFormula ? 'Validated by the server when you save.' : 'Optional for this calculation type.'}
              autoCapitalize="characters"
              autoCorrect={false}
              style={{ fontFamily: 'monospace' }}
              onChangeText={(v) => setF({ ...f, formula: v })}
              testID="component-formula"
            />
            <NumField label="Sort order" value={f.sort_order ?? 0} error={errs.sort_order} onChange={(v) => setF({ ...f, sort_order: v ?? 0 })} width={140} />
            <Stack gap={12}>
              <Switch label="Taxable" description="Included in taxable income for TDS" value={!!f.is_taxable} onChange={(v) => setF({ ...f, is_taxable: v })} />
              <Switch label="Counts towards PF wage" value={!!f.is_pf_wage} onChange={(v) => setF({ ...f, is_pf_wage: v })} />
              <Switch label="Counts towards ESI wage" value={!!f.is_esi_wage} onChange={(v) => setF({ ...f, is_esi_wage: v })} />
              <Switch label="Prorate" description="Reduce for LOP and partial months" value={!!f.prorate} onChange={(v) => setF({ ...f, prorate: v })} />
            </Stack>
            {!manage && <Text color={colors.muted}>Read only</Text>}
          </Stack>
        )}
      </Modal>
      <ConfirmDialog open={del} onClose={() => setDel(false)} onConfirm={() => remove.mutate(undefined)} loading={remove.isPending} title="Delete component?" message="Structures using this component must be updated first." danger confirmLabel="Delete" />
    </Screen>
  );
}
